/**
 * Utility functions for converting mention formats between legacy and TipTap
 *
 * Legacy format: @[Nome Cognome](user-uuid)
 * TipTap format: <span data-type="mention" data-id="uuid" data-label="Nome">@Nome</span>
 */

/**
 * Converts legacy mention format to TipTap HTML format
 *
 * @param content - Text with legacy mentions @[Nome](id)
 * @returns HTML with TipTap mention nodes
 *
 * @example
 * legacyToTiptap("Ciao @[Luca Bosso](uuid-123)!")
 * // Returns: 'Ciao <span data-type="mention" data-id="uuid-123" data-label="Luca Bosso">@Luca Bosso</span>!'
 */
export function legacyToTiptap(content: string): string {
  if (!content) return '';

  return content.replace(
    /@\[([^\]]+)\]\(([^)]+)\)/g,
    (match, label, id) => {
      // Escape HTML in label to prevent XSS
      const escapedLabel = label
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');

      return `<span data-type="mention" data-id="${id}" data-label="${escapedLabel}">@${escapedLabel}</span>`;
    }
  );
}

/**
 * Converts TipTap HTML mention nodes to legacy format
 *
 * @param html - HTML with TipTap mention nodes
 * @returns Text with legacy mentions @[Nome](id)
 *
 * @example
 * tiptapToLegacy('<span data-type="mention" data-id="uuid-123" data-label="Luca Bosso">@Luca Bosso</span>')
 * // Returns: '@[Luca Bosso](uuid-123)'
 */
export function tiptapToLegacy(html: string): string {
  if (!html) return '';

  // Use DOM parser to safely extract mention nodes
  if (typeof DOMParser !== 'undefined') {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const mentions = doc.querySelectorAll('[data-type="mention"]');
    mentions.forEach(mention => {
      const id = mention.getAttribute('data-id');
      const label = mention.getAttribute('data-label');

      if (id && label) {
        const textNode = document.createTextNode(`@[${label}](${id})`);
        mention.replaceWith(textNode);
      }
    });

    return doc.body.innerHTML;
  }

  // Fallback for server-side or environments without DOMParser
  return html.replace(
    /<span\s+data-type="mention"\s+data-id="([^"]+)"\s+data-label="([^"]+)"[^>]*>@[^<]*<\/span>/g,
    (match, id, label) => {
      // Unescape HTML entities in label
      const unescapedLabel = label
        .replace(/&quot;/g, '"')
        .replace(/&#039;/g, "'")
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&');

      return `@[${unescapedLabel}](${id})`;
    }
  );
}

/**
 * Extracts mention IDs from legacy format text
 *
 * @param content - Text with legacy mentions
 * @returns Array of user UUIDs
 *
 * @example
 * extractMentionIds("@[Luca](uuid-1) and @[Mario](uuid-2)")
 * // Returns: ['uuid-1', 'uuid-2']
 */
export function extractMentionIds(content: string): string[] {
  if (!content) return [];

  const mentionIds: string[] = [];
  const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
  let match;

  while ((match = mentionRegex.exec(content)) !== null) {
    const userId = match[2];
    if (userId && !mentionIds.includes(userId)) {
      mentionIds.push(userId);
    }
  }

  return mentionIds;
}
